import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import {
  CreateProgram,
  FilterResponse,
  FindManyProgram,
  FindManyProgramsSecure,
  FindOneProgram,
  Organisation,
  Option,
  PaginateResult,
  Program,
  RangeFilterResponse,
  UpdateAllProgram,
  UpdateProgram,
  UpdateResult,
} from '@univacity/shared/types';
import { ObjectUtil } from '@univacity/shared/util';
import { map, Observable } from 'rxjs';
import { Configuration } from '../config/configuration';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class ProgramService {
  private baseUrl: string;

  constructor(
    private http: HttpClient,
    private config: Configuration,
    private authService: AuthService
  ) {
    this.baseUrl = this.config.apiMainUrl + '/program';
  }

  findMinimal(
    query: FindManyProgram | FindManyProgramsSecure = {}
  ): Observable<PaginateResult<Program>> {
    const q = { ...query };
    if (!q.sort || q.sort?.includes('recommended')) {
      q['sort'] = [
        'views, -1',
        'excerpt, 1',
        'cost.commissionFee, -1',
        'description, -1',
        'name, -1',
        'cost.tuition, 1',
      ];
      if (q.search) q.sort = undefined;
    }

    const params = new HttpParams({
      fromObject: ObjectUtil.removeEmptyProperties({
        ...q,
        select: [
          'name',
          'cover',
          'status',
          'organisation',
          'format',
          'level',
          'attendance',
          'campuses',
          'duration',
          'intakes',
          'languages',
          'cost.tuition',
          'cost.currency',
          'cost.tuitionInterval',
        ],
        populate: [
          'intakes',
          'organisation.name',
          'organisation.isPartner',
          'organisation.logo',
          'organisation.address.country',
        ],
      }) as Params,
    });
    return this.http.get<PaginateResult<Program>>(this.baseUrl, {
      params: params,
    });
  }

  find(
    query: FindManyProgram | FindManyProgramsSecure = {}
  ): Observable<PaginateResult<Program>> {
    if (
      this.authService.isInstituteUser &&
      this.authService.user?.organisation
    ) {
      query.organisation = [this.authService.user.organisation];
    }
    const params = new HttpParams({
      fromObject: ObjectUtil.removeEmptyProperties(query) as Params,
    });
    return this.http.get<PaginateResult<Program>>(this.baseUrl, {
      params: params,
    });
  }

  count(
    query: FindManyProgram | FindManyProgramsSecure = {}
  ): Observable<number> {
    const params = new HttpParams({
      fromObject: ObjectUtil.removeEmptyProperties(query) as Params,
    });
    return this.http.get<number>(`${this.baseUrl}/count`, {
      params: params,
    });
  }

  filter(
    filter: string,
    query: FindManyProgram = {}
  ): Observable<PaginateResult<FilterResponse<string>>> {
    const params = new HttpParams({
      fromObject: ObjectUtil.removeEmptyProperties({
        ...query,
        filter,
      }) as Params,
    });
    return this.http.get<PaginateResult<FilterResponse<string>>>(
      `${this.baseUrl}/filter`,
      { params }
    );
  }

  range(
    key: string,
    query: FindManyProgram = {}
  ): Observable<RangeFilterResponse> {
    const params = new HttpParams({
      fromObject: ObjectUtil.removeEmptyProperties({ ...query, key }) as Params,
    });
    return this.http.get<RangeFilterResponse>(`${this.baseUrl}/range`, {
      params: params,
    });
  }

  findOne(
    program: string | Program,
    query?: FindOneProgram
  ): Observable<Program> {
    const id = typeof program === 'string' ? program : program._id;
    const q = { ...query };
    if (this.authService.isAdmin || this.authService.isInstituteUser)
      q.includeDraft = true;
    const params = new HttpParams({ fromObject: q as Params });
    return this.http.get<Program>(`${this.baseUrl}/${id}`, { params });
  }

  createProgram(program: CreateProgram): Observable<Program> {
    return this.http.post<Program>(
      this.baseUrl,
      ObjectUtil.removeEmptyProperties(program)
    );
  }

  deleteProgram(programID: string): Observable<void> {
    const organisationID = this.authService.user?.organisation;
    const params = new HttpParams({
      fromObject: ObjectUtil.removeEmptyProperties({
        organisation: organisationID,
      }),
    });

    return this.http.delete<void>(`${this.baseUrl}/${programID}`, {
      params,
    });
  }
  publishProgram(organisation: string | Organisation, programID: string) {
    const organisationID =
      typeof organisation === 'string' ? organisation : organisation._id;
    return this.http.patch<Pick<Program, '_id' | 'status'>>(
      `${this.baseUrl}/${programID}/publish`,
      {},
      {
        params: { organisation: organisationID },
      }
    );
  }
  updateProgram(
    programID: string,
    program: UpdateProgram
  ): Observable<Program> {
    return this.http.patch<Program>(
      `${this.baseUrl}/${programID}`,
      ObjectUtil.removeEmptyProperties(program)
    );
  }

  addToAll(update: UpdateAllProgram) {
    return this.http.patch<UpdateResult>(this.baseUrl, update);
  }
  removeFromAll(update: UpdateAllProgram) {
    return this.http.delete<UpdateResult>(this.baseUrl, {
      params: { ...update },
    });
  }

  autoComplete = (
    search: string,
    page?: number,
    query: FindManyProgram = {}
  ): Observable<{ options: Option[]; nextPage?: number | null }> => {
    const params = ObjectUtil.removeEmptyProperties({
      ...query,
      search,
      page,
      select: ['name'],
    });
    return this.http
      .get<PaginateResult<Program>>(this.baseUrl, {
        params,
      })
      .pipe(
        map((res) => {
          const options: Option[] = res.docs.map((i) => ({
            label: i.name,
            value: i._id,
          }));
          return { options, nextPage: res.nextPage };
        })
      );
  };
  nameAutoComplete = (
    search: string,
    page?: number,
    query: FindManyProgramsSecure = {}
  ): Observable<{ options: Option[]; nextPage?: number | null }> => {
    const params = ObjectUtil.removeEmptyProperties({
      ...query,
      search,
      page,
      select: ['name'],
    });
    return this.http
      .get<PaginateResult<string>>(this.baseUrl + '/autocomplete', {
        params,
      })
      .pipe(
        map((res) => {
          const options: Option[] = res.docs.map((name) => ({
            label: name,
            value: name,
          }));
          return { options, nextPage: res.nextPage };
        })
      );
  };
}
